import { QTooltip } from 'quasar'
import { getCurrentInstance } from '@/composition/vue/compositionApi'
import { getMonitoringService } from '@/core/container/ioc'
import TmLogicError from '@/core/error/tmLogicError'
import { LogLevel } from '@/core/logger/types'

Object.assign(QTooltip.props, {
  persistent: {
    type: Boolean,
    default: false,
  },
})

type QTooltipRenderResult = unknown[] | undefined
type QTooltipSetup = (props: Record<string, unknown>, ctx: unknown) => () => QTooltipRenderResult

/** Only the few Vue internals the diagnostics below read, so the coupling to them stays explicit. */
type VueInstance = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  type?: { name?: string; __name?: string }
  parent?: VueInstance | null
  subTree?: { el?: unknown } | null
}

const COMMENT_NODE = 8
const DOM_CHAIN_LIMIT = 6
const COMPONENT_CHAIN_LIMIT = 8
const BODY_CHILDREN_LIMIT = 12

/** Markers left behind by the tools that rewrite the DOM under Vue: page translators and extensions. */
const DOM_REWRITERS: Array<[string, string]> = [
  ['edgeTranslate', '[_msttexthash], [_msthash]'],
  ['googleTranslate', '#goog-gt-tt, html.translated-ltr, html.translated-rtl'],
  ['grammarly', '[data-gramm], grammarly-extension, grammarly-desktop-integration'],
]

let isReported = false

const hasMatch = (selector: string): boolean => {
  try {
    return document.querySelector(selector) !== null
  } catch {
    return false
  }
}

const describeElement = (el: Element): string => {
  const className = typeof el.className === 'string' ? el.className.trim() : ''
  const classes = className ? `.${className.split(/\s+/).join('.')}` : ''

  return `${el.tagName.toLowerCase()}${classes}`
}

const describeDomChain = (el: Element): string => {
  const parts: string[] = []
  let current: Element | null = el

  while (current && parts.length < DOM_CHAIN_LIMIT) {
    parts.push(describeElement(current))
    current = current.parentElement
  }

  return parts.join(' < ')
}

const describeComponentChain = (instance: VueInstance): string => {
  const names: string[] = []
  let current: VueInstance | null | undefined = instance

  while (current && names.length < COMPONENT_CHAIN_LIMIT) {
    // `__name` is emitted by the SFC compiler and survives the production build
    names.push(current.type?.name ?? current.type?.__name ?? 'Anonymous')
    current = current.parent
  }

  return names.join(' < ')
}

/** The detached placeholder has no parent left, so the surrounding DOM is reachable only through Vue. */
const findConnectedElement = (instance: VueInstance): Element | null => {
  let current: VueInstance | null | undefined = instance

  while (current) {
    const node = current.subTree?.el
    if (node instanceof Node && node.isConnected) {
      return node instanceof Element ? node : node.parentElement
    }
    current = current.parent
  }

  return null
}

/** Extensions inject their own roots next to the app's, so the body's own children are worth naming. */
const listBodyChildren = (): string[] =>
  Array.from(document.body.children).slice(0, BODY_CHILDREN_LIMIT).map(describeElement)

/**
 * Quasar renders a tooltip as a fragment while it is open and as a comment while it is closed, so every
 * show and hide changes the root vnode type — the one case where Vue needs a real parent node to patch
 * into. When code outside Vue takes the placeholder this component renders into out of its parent, that
 * parent is null: the patch throws halfway through, leaves the instance with a subtree that has no DOM
 * node, and every further update of it throws as well.
 *
 * Which tool does the rewriting is still unknown, and it cannot be told from the crash itself — by then
 * the detached node is unreachable. This render pass is the last moment it is still in hand, so the
 * surroundings are captured here instead, once per session.
 */
const reportDetachedPlaceholder = (instance: VueInstance, placeholder: Node, willOpen: boolean) => {
  isReported = true

  try {
    const isClosed = placeholder.nodeType === COMMENT_NODE
    const connectedElement = findConnectedElement(instance)

    getMonitoringService().logError(
      new TmLogicError('QTooltip placeholder was detached from the DOM by non-Vue code'),
      LogLevel.ERROR,
      {
        componentChain: describeComponentChain(instance),
        nearestConnectedDom: connectedElement ? describeDomChain(connectedElement) : null,
        tooltipWasOpen: !isClosed,
        // The root vnode type is about to change, which is what makes the next patch throw
        rootTypeChanges: isClosed === willOpen,
        domRewriters: DOM_REWRITERS.filter(([, selector]) => hasMatch(selector)).map(([name]) => name),
        bodyChildren: listBodyChildren(),
        documentLang: document.documentElement.lang,
        navigatorLanguages: navigator.languages?.join(','),
      },
    )
  } catch {
    // Diagnostics must never break rendering
  }
}

const patchedQTooltip = QTooltip as unknown as { setup: QTooltipSetup }
const originalSetup = patchedQTooltip.setup

patchedQTooltip.setup = (props, ctx) => {
  const render = originalSetup(props, ctx)
  const instance = getCurrentInstance() as unknown as VueInstance | null

  return () => {
    const result = render()

    if (!isReported && instance) {
      const placeholder = instance.subTree?.el
      if (placeholder instanceof Node && placeholder.parentNode === null) {
        reportDetachedPlaceholder(instance, placeholder, Array.isArray(result))
      }
    }

    return result
  }
}

export { QTooltip as QTooltipFixed }
